import React, { useMemo, useState } from "react";
import {
  BookOpen,
  ChevronRight,
  ExternalLink,
  Gamepad2,
  ListChecks,
  MonitorCog,
  Shield,
  ShieldAlert,
  Wrench,
} from "lucide-react";
import { SeoMeta, SiteFooter, SiteNavbar } from "../shared/site";
import "../index.css";

const MANUAL_SECTION_DATA = [
  {
    id: "bios",
    title: "BIOS",
    icon: MonitorCog,
    accent: "purple",
    pages: [
      {
        id: "ftpm-tpm",
        title: "Ftpm / Tpm",
        lead: "How to disable TPM",
        steps: [
          "Restart your computer.",
          "Enter BIOS/UEFI and press the key (usually F2, Delete, Esc or F10) immediately after turning on the computer.",
          "Find the TPM section. Look for sections named \"Security\", \"Trusted Computing\" or \"TPM\".",
          "Turn off the TPM. Find the option associated with the TPM and set the value to \"Disabled\".",
          "Save the changes. This is usually done using the F10 key to save the settings and restart.",
          "Restart your computer. After exiting BIOS/UEFI, the system will restart.",
        ],
      },
      {
        id: "secure-boot",
        title: "Secure Boot",
        lead: "How to disable Secure Boot",
        steps: [
          "Restart your PC.",
          "Enter BIOS/UEFI: Press F2, Delete, Esc or F10 (depending on your device).",
          "Find the section with Secure Boot: Usually in \"Security\" or \"Boot\".",
          "Disable Secure Boot: Set the value to \"Disabled\".",
          "Save the changes: Press F10 and confirm.",
        ],
      },
      {
        id: "uefi",
        title: "UEFI",
        blocks: [
          {
            lead: "A short guide for installing the uefi bios.",
          },
          {
            lead: "Step 1: Prepare",
            steps: ["Save your work.", "Restart the PC."],
          },
          {
            lead: "Step 2: Enter BIOS/UEFI",
            steps: [
              "Press the key to enter BIOS. Upon restarting the system, you often need to press one of the following keys (usually F2, Delete, Esc, or F10). A prompt may appear on the screen indicating which key to press.",
              "Open the BIOS/UEFI settings. If you successfully pressed the correct key, you will enter the BIOS menu.",
            ],
          },
          {
            lead: "Step 3: Find UEFI Settings",
            steps: [
              "Look for the \"Boot\" or \"Boot Options\" tab. In some systems, UEFI settings may be located in different sections, such as \"Advanced\" or \"System Configuration.\"",
              "Select the boot mode. Find the option related to the boot mode (usually called \"Boot Mode,\" \"UEFI/Legacy Boot,\" or something similar).",
            ],
          },
          {
            lead: "Step 4: Change Mode to UEFI",
            steps: [
              "Change the mode to UEFI. If the \"Legacy\" or \"CSM\" option is selected, change it to \"UEFI.\"",
              "Save the changes. Typically, this requires pressing the F10 key and selecting \"Yes\" to confirm.",
            ],
          },
          {
            lead: "Step 5: Reboot and Verify",
            steps: [
              "Restart the computer. After saving the settings, the system will reboot.",
              "Check the boot settings. Ensure that the system is booting in UEFI mode. You can do this by entering \"msinfo\" in the start menu and checking the \"BIOS mode\" tab.",
            ],
          },
          {
            lead: "Tips",
            steps: [
              "If BIOS/UEFI does not open, try using different keys during boot or check the documentation on your device manufacturer's website.",
            ],
          },
        ],
      },
      {
        id: "virtualization",
        title: "Virtualization",
        lead: "VIRTUALIZATION MUST BE ENABLED",
        intro: "How to enable virtualization",
        steps: [
          "Restart your computer.",
          "Log in to BIOS/UEFI.Press the key (usually F2, Delete, Esc or F10) immediately after turning on the device.",
          "Go to the processor settings.Find a section that can be called \"Advanced\", \"CPU Configuration\", \"Processor\" or similar.",
          "Enable virtualization. Find an option such as \"Intel VT\", \"Intel Virtualization Technology\", \"AMD-V\" or \"SVM Mode\" and change the value to \"Enabled\".",
          "Save the changes. This can usually be done by pressing F10 and then selecting \"Yes\" to confirm.",
          "Restart your computer.",
        ],
      },
    ],
  },
  {
    id: "windows-steps",
    title: "Windows Steps",
    icon: Shield,
    accent: "sky",
    pages: [
      {
        id: "core-isolation",
        title: "Core isolation",
        lead: "Settings => Update & Security => Windows security => Devise security => Core isolaction details",
      },
      {
        id: "dcontrol",
        title: "DControl",
        lead: "Download DControl",
        link: "/download/dcontrol.rar",
        steps: [
          "Download DConctol",
          "Run as an administrator",
          "Click on \"Disable\"",
          "The result should be like in the picture below!!!",
        ],
      },
      {
        id: "exploit-protection",
        title: "Exploit protection",
        lead: "Settings => Update & Security => Windows security => App & browser control => Exploits protection",
      },
      {
        id: "firewall-protection",
        title: "Firewall protection",
        lead: "Settings => Update & Security => Windows security => Firewall and browser control",
      },
      {
        id: "reputation-based-protection",
        title: "Reputation-based protection",
        lead: "Settings => Update & Security => Windows security => App & browser control => Reputatin-based protection",
      },
      {
        id: "uac",
        title: "UAC",
        lead: "Windows menu => write 'UAC' => Disable full",
      },
      {
        id: "virus-protection",
        title: "Virus protection",
        lead: "Settings => Update & Security => Windows security => Virus and threat protection",
      },
    ],
  },
  {
    id: "program",
    title: "Program",
    icon: Wrench,
    accent: "green",
    pages: [
      {
        id: "anti-cheats",
        title: "Anti-cheats",
        lead: "DELETE ALL",
        steps: [
          "Faceit ( Faceit client too)",
          "Vanguard ( Valorant and League of Legend games, as well as the RIOT launcher, must be REMOVED!)",
          "Esea",
          "Denuvo anti-cheat",
          "And others",
        ],
      },
      {
        id: "antivirus-programs",
        title: "Antivirus programs",
        lead: "DELETE ALL",
        steps: [
          "McAfee",
          "Norton",
          "McAfee",
          "Kaspersky",
          "Avast",
          "AVG",
          "ESET NOD32",
          "Malwarebytes",
          "360 secure",
          "ANY OTHER ANTIVIRUS SOFTWARE",
        ],
      },
      {
        id: "be-sure-to-install-it",
        title: "BE SURE TO INSTALL IT",
        lead: "Download libraries archive",
        link: "/download/libraries.zip",
      },
      {
        id: "overclocking-and-virtual-machihe",
        title: "Overclocking and Virtual Machihe",
        blocks: [
          {
            lead: "DELETE ALL Overclocking:",
            steps: [
              "MSI Afterburner",
              "EVGA Precision",
              "RivaTuner Statistics Server",
              "Gigabyte AORUS Engine",
              "ASUS GPU Tweak",
              "AMD Ryzen Master",
              "Intel Extreme Tuning Utility (XTU)",
              "CPU-Z",
              "Cinebench",
              "HWMonitor",
              "And others",
            ],
          },
          {
            lead: "Virtual machine",
            steps: [
              "VMware Workstation Pro",
              "Oracle VM VirtualBox",
              "Parallels Desktop",
              "QEMU",
              "KVM",
              "Citrix",
              "And others",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "overlays",
    title: "Overlays",
    icon: Gamepad2,
    accent: "pink",
    pages: [
      {
        id: "game-bar-and-game-mode",
        title: "Game bar and game mode",
        lead: "Below is how the settings in the Windows gaming section should look like",
        intro: "Windows settings => Gaming =< Game bar / Game mode",
      },
      {
        id: "most-popular-overlays",
        title: "Most popular overlays",
        steps: [
          "Discord",
          "NVIDIA GeForce Experience",
          "NVIDIA App",
          "Steam",
          "OBS Studio",
          "Twitch Studio",
          "Razer Cortex",
          "Xbox Game Bar",
          "Overwolf",
        ],
      },
    ],
  },
];

const sectionOrder = ["windows-steps", "program", "bios", "overlays"];
const sectionTitleOverrides = {
  "windows-steps": "WINDOWS",
  program: "PROGRAM",
  bios: "BIOS",
  overlays: "OVERLAYS",
};
const pageOrder = {
  "windows-steps": [
    "virus-protection",
    "firewall-protection",
    "reputation-based-protection",
    "exploit-protection",
    "core-isolation",
    "uac",
    "dcontrol",
  ],
  program: [
    "antivirus-programs",
    "anti-cheats",
    "overclocking-and-virtual-machihe",
    "be-sure-to-install-it",
  ],
  bios: ["uefi", "secure-boot", "ftpm-tpm", "virtualization"],
  overlays: ["game-bar-and-game-mode", "most-popular-overlays"],
};
const pageTitleOverrides = {
  "antivirus-programs": "Antivirus program",
  "overclocking-and-virtual-machihe": "Overclocking and Virtual machine",
  "be-sure-to-install-it": "Be sure to install it",
  "secure-boot": "Secure boot",
};

const sortByOrder = (items, order) =>
  [...items].sort((first, second) => {
    const firstIndex = order.indexOf(first.id);
    const secondIndex = order.indexOf(second.id);
    return (firstIndex === -1 ? Number.MAX_SAFE_INTEGER : firstIndex) - (secondIndex === -1 ? Number.MAX_SAFE_INTEGER : secondIndex);
  });

const MANUAL_SECTIONS = sortByOrder(MANUAL_SECTION_DATA, sectionOrder).map((section) => ({
  ...section,
  title: sectionTitleOverrides[section.id] || section.title,
  pages: sortByOrder(
    section.pages.filter((page) => !pageOrder[section.id] || pageOrder[section.id].includes(page.id)),
    pageOrder[section.id] || []
  ).map((page) => ({
    ...page,
    title: pageTitleOverrides[page.id] || page.title,
  })),
}));

const accentClasses = {
  purple: "border-purple-300/25 bg-purple-500/10 text-purple-100",
  sky: "border-sky-300/25 bg-sky-500/10 text-sky-100",
  green: "border-green-300/25 bg-green-500/10 text-green-100",
  pink: "border-pink-300/25 bg-pink-500/10 text-pink-100",
};

const MANUAL_SCREENSHOTS = {
  "game-bar-and-game-mode": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FfydXrJmpPqH8Dz9LXW4b%252FGAMEBAR.png%3Falt%3Dmedia%26token%3D86e8d2c3-303a-43b2-9710-5dd4cd45e1c2&width=768&dpr=3&quality=100&sign=1a964f47&sv=2",
      alt: "Game bar and game mode settings",
    },
  ],
  "core-isolation": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252F2txgwF6mAVLU3fmTd8nm%252Fcore.avif%3Falt%3Dmedia%26token%3D744a4ba8-d8a8-448f-8023-41c632080c1e&width=768&dpr=3&quality=100&sign=70a1c458&sv=2",
      alt: "Core isolation settings",
    },
  ],
  dcontrol: [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FI5K02CQWxkHTph7UbwXm%252Fdfc.png%3Falt%3Dmedia%26token%3D09dadbfc-92c8-4f32-9a1f-79ee95b47a31&width=768&dpr=3&quality=100&sign=1ed1ffd4&sv=2",
      alt: "DControl result",
    },
  ],
  "exploit-protection": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FUmIAcGa7mdgz1NB4nbbA%252Fexploit.avif%3Falt%3Dmedia%26token%3Dccca1439-a2f7-4fb7-92aa-bc893e281296&width=768&dpr=3&quality=100&sign=e49eda84&sv=2",
      alt: "Exploit protection settings",
    },
  ],
  "firewall-protection": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FO2rPJBF3VFIr6e241hDq%252Ffirewall.avif%3Falt%3Dmedia%26token%3D0b4f202c-c139-4373-b6e6-a5ef1cb5bfdb&width=768&dpr=3&quality=100&sign=c3d3fce7&sv=2",
      alt: "Firewall protection settings",
    },
  ],
  "reputation-based-protection": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FPEtKBIRsrgHoDgUebC02%252Fimage.avif%3Falt%3Dmedia%26token%3D930788a8-e14a-4696-b5fe-62f24fe2e45e&width=768&dpr=3&quality=100&sign=e7eed445&sv=2",
      alt: "Reputation-based protection settings",
    },
  ],
  uac: [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252Fdg0ucfu3YoZykGgd0S5A%252Fuac1.avif%3Falt%3Dmedia%26token%3D737c4fba-30ad-4462-93da-38514cb4a8cc&width=768&dpr=3&quality=100&sign=cd71bb83&sv=2",
      alt: "UAC search",
    },
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FcyLMmf0xo3lM1cShLDMb%252Fuac2.avif%3Falt%3Dmedia%26token%3D35b120d8-3908-4777-b302-b858cbb9a669&width=768&dpr=3&quality=100&sign=a95c55c&sv=2",
      alt: "UAC slider disabled",
    },
  ],
  "virus-protection": [
    {
      src: "https://hackseller.gitbook.io/hackseller/~gitbook/image?url=https%3A%2F%2F1157329941-files.gitbook.io%2F%7E%2Ffiles%2Fv0%2Fb%2Fgitbook-x-prod.appspot.com%2Fo%2Fspaces%252F9BPeiH1uIUfjXqMN7G6l%252Fuploads%252FO8T7OfcvTNhOOGNmYmh0%252Fvirus.png%3Falt%3Dmedia%26token%3D73ffe0a5-0ba8-4115-b29f-8ae0b1f9fd4f&width=768&dpr=3&quality=100&sign=1d497dae&sv=2",
      alt: "Virus and threat protection settings",
    },
  ],
};

function getPageBlocks(page) {
  if (page.blocks) return page.blocks;
  return [{ lead: page.lead, intro: page.intro, steps: page.steps, link: page.link, images: MANUAL_SCREENSHOTS[page.id] }];
}

function getSharperManualImage(src) {
  return src.replace("width=768", "width=1200").replace("dpr=3", "dpr=2");
}

function ManualContentBlock({ block }) {
  const isDownloadLink = block.link?.startsWith("/download/");

  return (
    <div className="rounded-lg border border-purple-400/15 bg-black/25 p-4">
      {block.lead && (
        <p className="text-2xl font-extrabold leading-snug text-white">
          {block.link ? (
            <a
              href={block.link}
              target={isDownloadLink ? undefined : "_blank"}
              rel={isDownloadLink ? undefined : "noopener noreferrer"}
              download={isDownloadLink ? true : undefined}
              className="inline-flex items-center gap-2 break-all text-purple-100 underline decoration-purple-300/50 underline-offset-4 hover:text-white"
            >
              {block.lead}
              <ExternalLink size={18} />
            </a>
          ) : (
            block.lead
          )}
        </p>
      )}
      {block.intro && <p className="mt-2 text-xl font-bold text-gray-300">{block.intro}</p>}
      {block.steps?.length > 0 && (
        <div className="mt-4 grid gap-2">
          {block.steps.map((step, index) => (
            <div key={`${step}-${index}`} className="flex gap-3 rounded-lg border border-purple-300/10 bg-gray-950/50 p-3 text-lg text-gray-200">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-purple-300/25 bg-purple-500/10 text-sm font-extrabold text-purple-100">
                {index + 1}
              </span>
              <span className="leading-snug">{step}</span>
            </div>
          ))}
        </div>
      )}
      {block.images?.length > 0 && (
        <div className="mt-4 grid gap-3">
          {block.images.map((image) => (
            <figure key={image.src} className="mx-auto w-full max-w-3xl overflow-hidden rounded-lg border border-purple-300/15 bg-gray-950/70 p-2">
              <a href={image.src} target="_blank" rel="noopener noreferrer" className="block">
                <img
                  src={getSharperManualImage(image.src)}
                  alt={image.alt}
                  className="mx-auto max-h-[520px] w-full object-contain"
                  loading="lazy"
                />
              </a>
              <figcaption className="mt-2 flex justify-end text-sm font-bold uppercase text-purple-200/75">
                <a href={image.src} target="_blank" rel="noopener noreferrer" className="hover:text-purple-100">
                  Open full image
                </a>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </div>
  );
}

function Manual() {
  const [activeSectionId, setActiveSectionId] = useState(MANUAL_SECTIONS[0].id);
  const [activePageId, setActivePageId] = useState(MANUAL_SECTIONS[0].pages[0].id);

  const activeSection = useMemo(
    () => MANUAL_SECTIONS.find((section) => section.id === activeSectionId) || MANUAL_SECTIONS[0],
    [activeSectionId]
  );
  const activePage = useMemo(
    () => activeSection.pages.find((page) => page.id === activePageId) || activeSection.pages[0],
    [activePageId, activeSection]
  );
  const ActiveIcon = activeSection.icon;

  const selectSection = (section) => {
    setActiveSectionId(section.id);
    setActivePageId(section.pages[0].id);
  };

  return (
    <div className="min-h-screen text-white font-jersey">
      <SeoMeta
        title="Manual"
        description="HackSeller setup manual mirrored from the official GitBook with BIOS, Windows, program and overlay steps."
        canonicalPath="/Manual"
      />
      <SiteNavbar />

      <main className="px-4 pb-16 pt-28 md:px-8">
        <section className="cinematic-panel mx-auto max-w-7xl overflow-hidden rounded-lg border border-purple-400/20 bg-gray-950/60 p-5 shadow-xl backdrop-blur-xl md:p-8">
          <div className="max-w-4xl">
            <div>
              <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-purple-300/30 bg-purple-500/10 px-3 py-1 text-sm font-extrabold uppercase text-purple-200">
                <BookOpen size={16} />
                Site manual
              </div>
              <h1 className="text-5xl font-extrabold leading-none md:text-7xl">HackSeller Manual</h1>
              <p className="mt-4 max-w-3xl text-xl leading-relaxed text-gray-300">
                BIOS, Windows steps, programs and overlays collected into one clean page for setup preparation.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href="https://hackseller.gitbook.io/hackseller/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-purple-300/20 bg-black/25 px-5 py-3 text-lg font-extrabold text-purple-100 transition hover:border-purple-200/50 hover:bg-purple-500/10"
                >
                  <ExternalLink size={19} />
                  Original GitBook
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-8 max-w-7xl rounded-lg border border-amber-300/25 bg-amber-500/10 p-5 shadow-[0_16px_45px_rgba(245,158,11,0.1)] backdrop-blur-xl md:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="max-w-4xl">
              <p className="flex items-center gap-2 text-sm font-extrabold uppercase text-amber-100">
                <ShieldAlert size={17} />
                Required to be performed without exceptions !
              </p>
              <h2 className="mt-2 text-3xl font-extrabold leading-tight text-white md:text-4xl">
                Technical support WILL NOT help you if you have NOT followed all the instructions!
              </h2>
              <p className="mt-3 text-xl font-bold text-amber-50/90">All points of this instruction are mandatory.</p>
              <p className="mt-2 text-lg text-amber-50/80">
                If the problem remains after completion, contact us in the discord group
              </p>
            </div>

            <div className="grid min-w-0 gap-3 lg:w-[24rem]">
              <div className="rounded-lg border border-amber-200/20 bg-black/25 p-3 text-lg font-bold text-amber-50">
                Most cheats work on ORIGINAL windows 10(11) pro!
              </div>
              <div className="rounded-lg border border-red-200/20 bg-red-500/10 p-3 text-lg font-bold text-red-50">
                Chance to run software on corporate/home/lite/repack/modded windows -practically zero.
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-8 grid max-w-7xl gap-5 lg:grid-cols-[0.34fr_0.66fr]">
          <aside className="rounded-lg border border-purple-400/20 bg-gray-950/65 p-4 shadow-[0_16px_45px_rgba(124,58,237,0.12)] lg:sticky lg:top-28 lg:self-start">
            <p className="mb-3 flex items-center gap-2 text-sm font-extrabold uppercase text-purple-200">
              <ListChecks size={16} />
              Manual index
            </p>
            <div className="grid gap-3">
              {MANUAL_SECTIONS.map((section) => {
                const Icon = section.icon;
                const isActiveSection = section.id === activeSection.id;

                return (
                  <div key={section.id} className="rounded-lg border border-purple-300/10 bg-black/20 p-2">
                    <button
                      type="button"
                      onClick={() => selectSection(section)}
                      className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-lg font-extrabold transition ${
                        isActiveSection
                          ? accentClasses[section.accent]
                          : "border-transparent text-gray-300 hover:border-purple-300/25 hover:bg-purple-500/10"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <Icon size={18} />
                        {section.title}
                      </span>
                      <span className="text-sm">{section.pages.length}</span>
                    </button>

                    {isActiveSection && (
                      <div className="mt-2 grid gap-1.5">
                        {section.pages.map((page) => {
                          const isActivePage = page.id === activePage.id;

                          return (
                            <button
                              key={page.id}
                              type="button"
                              onClick={() => setActivePageId(page.id)}
                              className={`flex items-center justify-between rounded-md px-3 py-2 text-left text-base font-bold transition ${
                                isActivePage ? "bg-white/10 text-white" : "text-gray-400 hover:bg-white/[0.04] hover:text-gray-200"
                              }`}
                            >
                              <span className="min-w-0 truncate">{page.title}</span>
                              {isActivePage && <ChevronRight size={16} className="shrink-0 text-purple-200" />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </aside>

          <article className="rounded-lg border border-purple-400/20 bg-gray-950/65 p-5 shadow-[0_16px_45px_rgba(124,58,237,0.12)] md:p-6">
            <div className="mb-5 flex items-center gap-3 border-b border-purple-300/10 pb-5">
              <div className="flex items-center gap-3">
                <span className={`flex h-12 w-12 items-center justify-center rounded-lg border ${accentClasses[activeSection.accent]}`}>
                  <ActiveIcon size={23} />
                </span>
                <div>
                  <p className="text-sm font-extrabold uppercase text-purple-200">{activeSection.title}</p>
                  <h2 className="text-4xl font-extrabold leading-none text-white">{activePage.title}</h2>
                </div>
              </div>
            </div>

            <div className="grid gap-4">
              {getPageBlocks(activePage).map((block, index) => (
                <ManualContentBlock key={`${activePage.id}-${index}`} block={block} />
              ))}
            </div>

            <div className="mt-5 rounded-lg border border-amber-300/20 bg-amber-500/10 p-4 text-amber-50">
              <p className="flex items-center gap-2 text-sm font-extrabold uppercase">
                <ShieldAlert size={16} />
                Manual note
              </p>
              <p className="mt-2 text-lg leading-snug text-amber-50/90">
                Follow product-specific support instructions when they differ from this general manual.
              </p>
            </div>
          </article>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}

export default Manual;
